import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Step data for business description
export interface BusinessDescriptionData {
    businessName: string;
    businessStage: 'Pre-Revenue Startup' | 'Early Stage Startup' | 'Growth Stage' | 'Established Business';
    businessDescription: string;
    targetCustomers: string;
    revenueModel: string;
    keyMetrics: string;
    majorCosts: string;
    growthDrivers: string;
    currency: 'USD' | 'EUR' | 'GBP' | 'INR' | 'AUD' | 'CAD';
    complexity: 'Simple' | 'Standard' | 'Advanced';
}

const initialFormData: BusinessDescriptionData = {
    businessName: '',
    businessStage: 'Early Stage Startup',
    businessDescription: '',
    targetCustomers: '',
    revenueModel: '',
    keyMetrics: '',
    majorCosts: '',
    growthDrivers: '',
    currency: 'USD',
    complexity: 'Standard',
};

interface CustomModelFormProps {
    onSubmit: (data: BusinessDescriptionData) => void;
    isLoading: boolean;
}

const STEPS = [
    { id: 1, title: 'Basics', description: 'Name & Stage' },
    { id: 2, title: 'Business Model', description: 'Describe Your Business' },
    { id: 3, title: 'Revenue & Costs', description: 'Financial Structure' },
    { id: 4, title: 'Generate', description: 'Review & Generate' },
];

const STAGES = [
    { value: 'Pre-Revenue Startup', label: '🌱 Pre-Revenue Startup', desc: "Haven't started selling yet" },
    { value: 'Early Stage Startup', label: '🚀 Early Stage Startup', desc: '< $1M annual revenue' },
    { value: 'Growth Stage', label: '📈 Growth Stage', desc: '$1M - $10M annual revenue' },
    { value: 'Established Business', label: '🏢 Established Business', desc: '> $10M annual revenue' },
];

const CURRENCIES = [
    { value: 'USD', label: '🇺🇸 USD - US Dollar' },
    { value: 'EUR', label: '🇪🇺 EUR - Euro' },
    { value: 'GBP', label: '🇬🇧 GBP - British Pound' },
    { value: 'INR', label: '🇮🇳 INR - Indian Rupee' },
    { value: 'AUD', label: '🇦🇺 AUD - Australian Dollar' },
    { value: 'CAD', label: '🇨🇦 CAD - Canadian Dollar' },
];

const COMPLEXITY = [
    { value: 'Simple', label: 'Simple', desc: '15-25 assumptions, basic model' },
    { value: 'Standard', label: 'Standard', desc: '35-50 assumptions, comprehensive' },
    { value: 'Advanced', label: 'Advanced', desc: '50+ assumptions, detailed analysis' },
];

/**
 * Multi-step form for describing business and generating custom financial model
 */
export function CustomModelForm({ onSubmit, isLoading }: CustomModelFormProps) {
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState<BusinessDescriptionData>(initialFormData);
    const [errors, setErrors] = useState<Partial<Record<keyof BusinessDescriptionData, string>>>({});

    const updateField = useCallback(<K extends keyof BusinessDescriptionData>(
        field: K,
        value: BusinessDescriptionData[K]
    ) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error when field is updated
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    }, [errors]);

    const validateStep = (step: number): boolean => {
        const newErrors: Partial<Record<keyof BusinessDescriptionData, string>> = {};

        if (step === 1) {
            if (!formData.businessName.trim()) {
                newErrors.businessName = 'Business name is required';
            }
        }

        if (step === 2) {
            if (!formData.businessDescription.trim()) {
                newErrors.businessDescription = 'Please describe your business';
            } else if (formData.businessDescription.trim().length < 30) {
                newErrors.businessDescription = 'Please provide more detail (at least 30 characters)';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const nextStep = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
        }
    };

    const prevStep = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
    };

    const handleSubmit = () => {
        if (validateStep(currentStep)) {
            onSubmit(formData);
        }
    };

    return (
        <div className="custom-model-form">
            {/* Progress Steps */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    {STEPS.map((step, index) => (
                        <div key={step.id} className="flex items-center flex-1">
                            <div className="flex flex-col items-center">
                                <motion.div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${currentStep >= step.id
                                            ? 'bg-primary-600 text-white'
                                            : 'bg-slate-200 text-slate-500'
                                        }`}
                                    animate={{ scale: currentStep === step.id ? 1.1 : 1 }}
                                >
                                    {currentStep > step.id ? '✓' : step.id}
                                </motion.div>
                                <div className="mt-2 text-center hidden sm:block">
                                    <p className={`text-sm font-medium ${currentStep >= step.id ? 'text-slate-900' : 'text-slate-400'
                                        }`}>
                                        {step.title}
                                    </p>
                                    <p className="text-xs text-slate-400">{step.description}</p>
                                </div>
                            </div>
                            {index < STEPS.length - 1 && (
                                <div className={`flex-1 h-1 mx-4 rounded ${currentStep > step.id ? 'bg-primary-600' : 'bg-slate-200'
                                    }`} />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Step Content */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white rounded-xl p-6 border border-slate-200"
                >
                    {/* Step 1: Basics */}
                    {currentStep === 1 && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-2">Let's Get Started</h2>
                                <p className="text-slate-600">Tell us about your business basics</p>
                            </div>

                            {/* Business Name */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Business Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.businessName}
                                    onChange={(e) => updateField('businessName', e.target.value)}
                                    placeholder="e.g., BeanBox Coffee, Strategic Partners Inc."
                                    className={`w-full px-4 py-3 rounded-lg border ${errors.businessName ? 'border-red-300' : 'border-slate-300'
                                        } focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all`}
                                />
                                {errors.businessName && (
                                    <p className="mt-1 text-sm text-red-600">{errors.businessName}</p>
                                )}
                            </div>

                            {/* Business Stage */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Business Stage
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {STAGES.map((stage) => (
                                        <div
                                            key={stage.value}
                                            onClick={() => updateField('businessStage', stage.value as BusinessDescriptionData['businessStage'])}
                                            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${formData.businessStage === stage.value
                                                    ? 'border-primary-500 bg-primary-50'
                                                    : 'border-slate-200 hover:border-slate-300'
                                                }`}
                                        >
                                            <p className="font-medium text-slate-900">{stage.label}</p>
                                            <p className="text-sm text-slate-500">{stage.desc}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Currency & Complexity */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Currency
                                    </label>
                                    <select
                                        value={formData.currency}
                                        onChange={(e) => updateField('currency', e.target.value as BusinessDescriptionData['currency'])}
                                        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                                    >
                                        {CURRENCIES.map((c) => (
                                            <option key={c.value} value={c.value}>{c.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Model Complexity
                                    </label>
                                    <select
                                        value={formData.complexity}
                                        onChange={(e) => updateField('complexity', e.target.value as BusinessDescriptionData['complexity'])}
                                        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                                    >
                                        {COMPLEXITY.map((c) => (
                                            <option key={c.value} value={c.value}>{c.label} - {c.desc}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Business Model */}
                    {currentStep === 2 && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-2">Describe Your Business</h2>
                                <p className="text-slate-600">The more detail you provide, the better your model will be</p>
                            </div>

                            {/* Business Description */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    What does your business do? *
                                </label>
                                <textarea
                                    value={formData.businessDescription}
                                    onChange={(e) => updateField('businessDescription', e.target.value)}
                                    placeholder="Describe your business in detail. What products or services do you offer? What makes you unique? For example: 'We are a direct-to-consumer subscription service delivering freshly roasted specialty coffee beans weekly. We source from single-origin farms and roast in small batches...'"
                                    rows={4}
                                    className={`w-full px-4 py-3 rounded-lg border ${errors.businessDescription ? 'border-red-300' : 'border-slate-300'
                                        } focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all resize-y`}
                                />
                                {errors.businessDescription && (
                                    <p className="mt-1 text-sm text-red-600">{errors.businessDescription}</p>
                                )}
                                <p className="mt-1 text-xs text-slate-400">
                                    {formData.businessDescription.length} characters
                                </p>
                            </div>

                            {/* Target Customers */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Who are your target customers?
                                </label>
                                <input
                                    type="text"
                                    value={formData.targetCustomers}
                                    onChange={(e) => updateField('targetCustomers', e.target.value)}
                                    placeholder="e.g., Urban professionals aged 25-45, mid-market B2B companies"
                                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                                />
                            </div>
                        </div>
                    )}

                    {/* Step 3: Revenue & Costs */}
                    {currentStep === 3 && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-2">Financial Structure</h2>
                                <p className="text-slate-600">Help us understand your revenue and cost drivers</p>
                            </div>

                            {/* Revenue Model */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    How do you make money?
                                </label>
                                <input
                                    type="text"
                                    value={formData.revenueModel}
                                    onChange={(e) => updateField('revenueModel', e.target.value)}
                                    placeholder="e.g., Monthly subscription ($29/mo), Per-transaction fees (2.9%), Project-based ($150K avg)"
                                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                                />
                            </div>

                            {/* Key Metrics */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Key business metrics
                                </label>
                                <input
                                    type="text"
                                    value={formData.keyMetrics}
                                    onChange={(e) => updateField('keyMetrics', e.target.value)}
                                    placeholder="e.g., MRR, LTV, CAC, churn rate, utilization rate"
                                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                                />
                            </div>

                            {/* Major Costs */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Major cost categories
                                </label>
                                <input
                                    type="text"
                                    value={formData.majorCosts}
                                    onChange={(e) => updateField('majorCosts', e.target.value)}
                                    placeholder="e.g., Raw materials, hosting costs, salaries, marketing, rent"
                                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                                />
                            </div>

                            {/* Growth Drivers */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Growth drivers
                                </label>
                                <input
                                    type="text"
                                    value={formData.growthDrivers}
                                    onChange={(e) => updateField('growthDrivers', e.target.value)}
                                    placeholder="e.g., Marketing campaigns, referral program, new markets, product expansion"
                                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                                />
                            </div>
                        </div>
                    )}

                    {/* Step 4: Review & Generate */}
                    {currentStep === 4 && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 mb-2">Review & Generate</h2>
                                <p className="text-slate-600">Confirm your details and generate your custom model</p>
                            </div>

                            {/* Summary */}
                            <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-slate-500">Business Name</p>
                                        <p className="font-medium text-slate-900">{formData.businessName || '—'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500">Stage</p>
                                        <p className="font-medium text-slate-900">{formData.businessStage}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500">Currency</p>
                                        <p className="font-medium text-slate-900">{formData.currency}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500">Complexity</p>
                                        <p className="font-medium text-slate-900">{formData.complexity}</p>
                                    </div>
                                </div>

                                <hr className="border-slate-200" />

                                <div>
                                    <p className="text-sm text-slate-500 mb-1">Business Description</p>
                                    <p className="text-slate-700">{formData.businessDescription || '—'}</p>
                                </div>

                                {formData.targetCustomers && (
                                    <div>
                                        <p className="text-sm text-slate-500 mb-1">Target Customers</p>
                                        <p className="text-slate-700">{formData.targetCustomers}</p>
                                    </div>
                                )}

                                {formData.revenueModel && (
                                    <div>
                                        <p className="text-sm text-slate-500 mb-1">Revenue Model</p>
                                        <p className="text-slate-700">{formData.revenueModel}</p>
                                    </div>
                                )}
                            </div>

                            {/* What happens next */}
                            <div className="bg-primary-50 rounded-xl p-6 border border-primary-100">
                                <h3 className="font-semibold text-primary-900 mb-3">🚀 What happens next?</h3>
                                <ul className="space-y-2 text-sm text-primary-800">
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary-600">1.</span>
                                        Our AI analyzes your business and generates tailored assumptions
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary-600">2.</span>
                                        You can customize any assumption before generating the model
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary-600">3.</span>
                                        Download a complete Excel model with all formulas linked
                                    </li>
                                </ul>
                            </div>
                        </div>
                    )}
                </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-6">
                <button
                    onClick={prevStep}
                    disabled={currentStep === 1}
                    className={`px-6 py-3 rounded-lg font-medium transition-all ${currentStep === 1
                            ? 'text-slate-300 cursor-not-allowed'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                        }`}
                >
                    ← Back
                </button>

                {currentStep < STEPS.length ? (
                    <button
                        onClick={nextStep}
                        className="px-8 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors shadow-lg shadow-primary-600/20"
                    >
                        Continue →
                    </button>
                ) : (
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className="px-8 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-purple-700 transition-all shadow-lg shadow-primary-600/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isLoading ? (
                            <>
                                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Generating Model...
                            </>
                        ) : (
                            <>
                                🚀 Generate Custom Model
                            </>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
}
